import { ChangeEvent, FC, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Chart from 'react-apexcharts';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme
} from '@mui/material';
import { merge } from 'lodash'
import { DatabaseResponseDto } from '@/gen/api/backup';
import { tbgSxProps } from '../overrides/toggleButtonGroupSx';
import { useMetricsContext } from '@/pages/databases/useMetricsController';
import { TimeRangeType } from '@/gen/api/metrics';
import { ChartLegends, generalizeData, genericChartOptions, LineChartDataEx, perfThreshold, samplingLineChartData } from './metricsChartCommon';


export interface TopRowCountChartProps {
  databaseInfo: DatabaseResponseDto;
}

const TopRowCountChart: FC<TopRowCountChartProps> = ({
  databaseInfo,
}) => {
  const theme = useTheme();
  const {
    rowcountData,
    observeRowcount,
    timeRange,
    setTimeRange,
  } = useMetricsContext()
  
  useEffect(() => {
    observeRowcount(true)
    setTimeRange(TimeRangeType.Week)
  }, [observeRowcount, setTimeRange])

  const finalData = useMemo(() => 
    samplingLineChartData(
      generalizeData(theme, rowcountData), 
      timeRange
    ), [theme, rowcountData, timeRange])

  const [selectedSeries, setSelectedSeries] = useState(finalData.legend);
  const selectedSeriesRef = useRef(selectedSeries)
  useEffect(() => {
    setSelectedSeries(finalData.legend)
  }, [finalData])
  
  useEffect(() => {
    selectedSeriesRef.current = selectedSeries
  }, [selectedSeries])
  const handleChange = useCallback((x: LineChartDataEx, checked: boolean) => {
    if (!checked) {
      setSelectedSeries(selectedSeriesRef.current.filter((item) => item !== x.name));
    } else {
      setSelectedSeries([...selectedSeriesRef.current, x.name]);
    }
  }, []);

  const maxValueMap = useMemo(() => 
    finalData.series.reduce((acc, s) => {
      acc[s.name] = Math.max.apply(Math, s.data)
      return acc
    }, {} as Record<string, number>), [finalData])

  const visibleSeries = useMemo(() => finalData.series.filter((item) => item.name && selectedSeries.includes(item.name)), [finalData, selectedSeries]);

  const maxValue = visibleSeries.length > 0 ? Math.max.apply(Math, visibleSeries.map(s => maxValueMap[s.name])) : 0
  const units = ["", "K", "M", "B"]
  const [unit, offsetRank] = (() => {
    const ls = units.flatMap((u, i) => (maxValue / (Math.pow(1000, i))) > 1 ? [[u, i] as const] : []);
    return ls.length > 0 ? ls[ls.length - 1] : (["K", 0] as const)
  })();
  const shrinkFactor = Math.pow(1000, offsetRank)
  
  const genericOptions = useMemo(() => genericChartOptions(theme), [theme])

  const chart = useMemo(() => {
    const datapointCount = visibleSeries.map(s => s.data.length).reduce((acc, n) => acc + n, 0)

    const erase = {
      options: {
        ...(datapointCount > perfThreshold ? { markers: null } : {}),
      },
    }
    const patch = {
      options: {
        ...(datapointCount > perfThreshold ? { 
          markers: { size: 0 },
        } : {}),
      },
    }
    return merge({}, genericOptions, {
      options: {
        colors: visibleSeries.map((item) => item.color),
        xaxis: {
          type: 'datetime',
          categories: finalData.xaxis,
        },
        yaxis: [{
          title: {
            text: `Row Count`
          },
          labels: {
            formatter: (...value:any) => { 
              return (Number.isInteger(value[0]) ? value[0] : Number(value[0]).toFixed(1)) + unit
            },
          },
        }],
      },
      series: visibleSeries.map(s => ({
        ...s,
        data: s.data.map(d => Number((d / shrinkFactor).toFixed(1)))
      })),
    }, erase, patch)
  }, [genericOptions, visibleSeries, finalData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
      }}
    >
      <Card>
        <CardHeader
          title="Row Count of Top 5 Tables"
          action={
            <ToggleButtonGroup
              size="small"
              sx={{
                ...tbgSxProps,
                mr: 2,
              }}
              exclusive
              value={timeRange}
              onChange={(e:any, v:TimeRangeType) => setTimeRange(v)}
            >
              <ToggleButton value={TimeRangeType.Week}>
                <Typography variant="subtitle2" color="textSecondary">W</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Month}>
                <Typography variant="subtitle2" color="textSecondary">M</Typography>
              </ToggleButton>
              <ToggleButton value={TimeRangeType.Year}>
                <Typography variant="subtitle2" color="textSecondary">Y</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          <ChartLegends series={finalData.series} onClick={handleChange} />
          <Chart
            height="350"
            type="line"
            {...chart}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TopRowCountChart;